/**
 * FILE: apps/admin/src/lib/humanPageHints.ts
 * PURPOSE: Plain-language hints + CTA labels for page status banners when the
 *          backend omits topPriorityLabel or for client-side fallbacks.
 */

import type { PdcaStageId } from './pdca'
import {
  bottleneckActionLabel,
  bottleneckHumanHeadline,
  bottleneckHumanHint,
  type BottleneckContext,
} from './pdcaBottleneck'

export function scopedHref(base: string, projectId: string | null | undefined): string {
  if (!projectId) return base
  const [path, qs = ''] = base.split('?')
  const params = new URLSearchParams(qs)
  params.set('project', projectId)
  const merged = params.toString()
  return merged ? `${path}?${merged}` : path
}

/** Headline for reports still unfixed after their last auto-fix attempt (counted per report). */
export function fixesFailedTitle(count: number): string {
  return `Auto-fix stopped on ${count} report${count === 1 ? '' : 's'}`
}

export function fixesFailedHint(count: number): string {
  return count === 1
    ? 'The last fix attempt on this report stopped before a PR merged. Open it to read why, then retry or fix it in your editor.'
    : 'The last fix attempt on these reports stopped before a PR merged. Open each one to read why, then retry or fix it in your editor.'
}

export function fixesFailedAction(count: number): string {
  return count === 1 ? 'See why it stopped' : `See why (${count})`
}

export function triageBacklogHint(count: number): string {
  return count === 1
    ? 'This report is waiting for triage. Triage it so auto-fix can pick it up.'
    : 'These reports are waiting for triage. Triage the oldest first so auto-fix can pick them up.'
}

export function judgeDisagreementHint(ratePct: number): string {
  return `The judge disagreed with the classifier on ${ratePct}% of recent grades. Review mismatches before merging fixes.`
}

export function integrationIssuesHint(count: number): string {
  return count === 1
    ? 'A GitHub, webhook, or Sentry connection is failing — fixes may not land until this is green.'
    : `${count} integrations are failing health checks — fixes may not land until connections recover.`
}

export function llmErrorsHint(ratePct: number): string {
  return `${ratePct}% of AI calls failed recently. Check your API keys in Settings or inspect the failing model on the LLM tab.`
}

export function cronErrorsHint(count: number): string {
  return count === 1
    ? 'A scheduled background job failed. Open Cron to see which job broke and when it last ran.'
    : `${count} scheduled jobs failed. Open Cron to see which jobs broke and when they last ran.`
}

export function qaFailingHint(count: number): string {
  return count === 1
    ? 'One user-story test is failing on schedule. Open the run to see screenshots and assertion errors.'
    : `${count} user-story tests are failing. Open the failing runs to see screenshots and assertion errors.`
}

export function deadLetterHint(count: number): string {
  return count === 1
    ? 'One report hit the dead-letter queue after retries were exhausted. Inspect before replaying.'
    : `${count} reports hit the dead-letter queue. Inspect each before replaying — they will not retry automatically.`
}

export function driftCriticalHint(count: number): string {
  return count === 1
    ? 'Your app docs, routes, and database no longer agree — fix this before users hit broken endpoints.'
    : `${count} API mismatches need review — your docs, routes, and database no longer agree.`
}

export function driftWarnHint(count: number): string {
  return count === 1
    ? 'One warning-level mismatch is open — review before it becomes a breaking change.'
    : `${count} warning-level mismatches are open — review before they become breaking changes.`
}

export function driftNeverScannedHint(): string {
  return 'Run one scan so Mushi knows what your API and database should look like.'
}

export function driftStaleHint(): string {
  return 'Your last scan is old — run a fresh scan to catch recent API or schema changes.'
}

export function driftHealthyHint(): string {
  return 'OpenAPI, inventory, and database contracts match the last snapshot.'
}

export function codeHealthNoDataHint(projectLabel: string): string {
  return `Add the ingest key to ${projectLabel}'s GitHub Actions — bundle and file-size numbers appear after your next push to main.`
}

export function codeHealthErrorsHint(count: number): string {
  return count === 1
    ? 'One file is over 2,000 lines — split it before your next release.'
    : `${count} files are over 2,000 lines — split them before your next release.`
}

export function codeHealthWarningsHint(count: number): string {
  return count === 1
    ? 'One file is approaching the 2,000-line budget — plan a split soon.'
    : `${count} files are approaching the 2,000-line budget — plan splits soon.`
}

export function anomaliesNoMetricsHint(): string {
  return 'Send error rate, latency, or conversion metrics — then Mushi can flag unusual jumps.'
}

export function anomaliesCriticalHint(count: number): string {
  return count === 1
    ? 'One metric spike may signal a release regression — confirm or dismiss it.'
    : `${count} metric spikes need review — confirm real regressions or dismiss noise.`
}

export function anomaliesOpenHint(count: number): string {
  return count === 1
    ? 'Something changed compared to the usual pattern — open it to confirm or dismiss.'
    : `${count} metric spikes need review — something changed compared to the usual pattern.`
}

export function bottleneckFromSnapshot(ctx: BottleneckContext & { projectId?: string | null }) {
  return {
    headline: bottleneckHumanHeadline(ctx),
    hint: bottleneckHumanHint(ctx),
    actionLabel: bottleneckActionLabel(ctx),
  }
}

export type { BottleneckContext, PdcaStageId }
